import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, MapPin, Phone, Mail, Globe, User, Pencil, Navigation,
} from "lucide-react";
import api from "../lib/api";
import { fmtCurrency, fmtDate, statusClass } from "../lib/format";
import { mapsSearch } from "../lib/integrations";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const TABS = ["Overview", "Contacts", "Meetings", "Opportunities", "Orders", "Training", "Devices", "Documents", "Notes", "Tasks"];

function EmptyRow({ text }) {
  return <div className="text-center text-slate-400 py-10 text-sm">{text}</div>;
}

function MiniTable({ headers, rows, render }) {
  if (!rows.length) return <EmptyRow text="No records yet" />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-100">
          {headers.map((h) => <th key={h} className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-3 py-2.5 whitespace-nowrap">{h}</th>)}
        </tr></thead>
        <tbody>{rows.map((r, i) => <tr key={r.id || i} className="border-b border-slate-50 hover:bg-slate-50/60">{render(r)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export default function ClinicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [data, setData] = useState({ contacts: [], meetings: [], opportunities: [], devices: [], documents: [], tasks: [], workshops: [] });

  useEffect(() => {
    api.get(`/clinics/${id}`).then((r) => setClinic(r.data)).catch(() => navigate("/clinics"));
    Promise.all([
      api.get("/contacts"), api.get("/meetings"), api.get("/opportunities"),
      api.get("/devices"), api.get("/documents"), api.get("/tasks"), api.get("/workshops"),
    ]).then(([c, m, o, d, doc, t, w]) => {
      setData({ contacts: c.data, meetings: m.data, opportunities: o.data, devices: d.data, documents: doc.data, tasks: t.data, workshops: w.data });
    });
  }, [id, navigate]);

  if (!clinic) return <div className="text-slate-400">Loading clinic…</div>;

  const f = (arr) => arr.filter((x) => x.clinic_id === id || x.clinic_name === clinic.name);
  const contacts = f(data.contacts), meetings = f(data.meetings), opps = f(data.opportunities);
  const devices = f(data.devices), tasks = f(data.tasks);
  const wonOpps = opps.filter((o) => o.stage === "Won");

  return (
    <div>
      <button onClick={() => navigate("/clinics")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy-900 mb-4" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Clinics
      </button>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="h-14 w-14 rounded-2xl bg-navy-900 flex items-center justify-center shrink-0">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold text-navy-900 tracking-tight">{clinic.name}</h1>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusClass(clinic.status)}`}>{clinic.status}</span>
            </div>
            <p className="text-sm text-slate-400 mt-0.5">{clinic.trading_name} · {clinic.clinic_type}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {clinic.address}, {clinic.city}, {clinic.region}</span>
              <a href={mapsSearch(`${clinic.name} ${clinic.address} ${clinic.city} ${clinic.region}`)} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-brand hover:underline font-medium" data-testid="clinic-directions">
                <Navigation className="h-4 w-4" /> Directions
              </a>
              {clinic.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-slate-400" /> {clinic.phone}</span>}
              {clinic.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-slate-400" /> {clinic.email}</span>}
              {clinic.website && <span className="flex items-center gap-1.5"><Globe className="h-4 w-4 text-slate-400" /> {clinic.website}</span>}
              <span className="flex items-center gap-1.5"><User className="h-4 w-4 text-slate-400" /> BDM: {clinic.assigned_bdm_name || "—"}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-100">
          {[["Contacts", contacts.length], ["Opportunities", opps.length], ["Won Revenue", fmtCurrency(wonOpps.reduce((a, o) => a + Number(o.value || 0), 0))], ["Devices", devices.length]].map(([l, v]) => (
            <div key={l}><div className="font-display text-xl font-bold text-navy-900">{v}</div><div className="text-xs text-slate-400">{l}</div></div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="Overview">
        <TabsList className="h-auto flex-wrap justify-start bg-white border border-slate-200 p-1 rounded-xl gap-1 overflow-x-auto w-full">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`tab-${t.toLowerCase()}`}
              className="data-[state=active]:bg-navy-900 data-[state=active]:text-white rounded-lg text-xs sm:text-sm px-3 py-1.5">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mt-4">
          <TabsContent value="Overview">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Details</h4>
                <dl className="space-y-2 text-sm">
                  {[["Trading Name", clinic.trading_name], ["Type", clinic.clinic_type], ["Suburb", clinic.suburb], ["Postal Code", clinic.postal_code], ["Country", clinic.country]].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-50 pb-2"><dt className="text-slate-400">{k}</dt><dd className="font-medium text-navy-900">{v || "—"}</dd></div>
                  ))}
                </dl>
              </div>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2">Notes</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{clinic.notes || "No notes recorded for this clinic."}</p>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="Contacts">
            <MiniTable headers={["Name", "Position", "Role", "Email", "Mobile"]} rows={contacts}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.first_name} {r.last_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.position}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.contact_role}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.email}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.mobile}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Meetings">
            <MiniTable headers={["Date", "Type", "Location", "Follow-up"]} rows={meetings}
              render={(r) => (<>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.meeting_type}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.location}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.follow_up_date)}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Opportunities">
            <MiniTable headers={["Product", "Stage", "Value", "Discount", "Prob."]} rows={opps}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.product}</td>
                <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(r.stage)}`}>{r.stage}</span></td>
                <td className="px-3 py-2.5 font-semibold text-navy-900">{fmtCurrency(r.value)}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.discount || 0}%</td>
                <td className="px-3 py-2.5 text-slate-600">{r.probability}%</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Orders">
            <MiniTable headers={["Product", "Value", "Status", "Closed"]} rows={wonOpps}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.product}</td>
                <td className="px-3 py-2.5 font-semibold text-navy-900">{fmtCurrency(r.value)}</td>
                <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(r.invoice_paid ? "Paid" : "Pending")}`}>{r.invoice_paid ? "Paid" : "Unpaid"}</span></td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.close_date)}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Training">
            <MiniTable headers={["Event", "Date", "Trainer", "Certificates"]} rows={data.workshops}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.event_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.date)}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.trainer_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.certificates_issued}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Devices">
            <MiniTable headers={["Device", "Serial", "Installed", "Warranty"]} rows={devices}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.device_type}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.serial_number}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.install_date)}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.warranty_expiry)}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Documents">
            <MiniTable headers={["Name", "Category", "Type"]} rows={data.documents}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.name}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.category}</td>
                <td className="px-3 py-2.5 text-slate-600 uppercase">{r.file_type}</td>
              </>)} />
          </TabsContent>
          <TabsContent value="Notes">
            <p className="text-sm text-slate-600 leading-relaxed">{clinic.notes || "No notes recorded."}</p>
          </TabsContent>
          <TabsContent value="Tasks">
            <MiniTable headers={["Task", "Assigned", "Due", "Priority", "Status"]} rows={tasks}
              render={(r) => (<>
                <td className="px-3 py-2.5 font-medium text-navy-900">{r.title}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.assigned_to_name}</td>
                <td className="px-3 py-2.5 text-slate-600">{fmtDate(r.due_date)}</td>
                <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(r.priority)}`}>{r.priority}</span></td>
                <td className="px-3 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${statusClass(r.status)}`}>{r.status}</span></td>
              </>)} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
